import classNames from "classnames";
import PaperDebugInfo from "components/PaperDebugInfo/PaperDebugInfo";
import Link from "next/link";
import { SearchResultViewProps } from "../../types";
import PaperTitle from "../shared/PaperTitle";
import SearchResultPaperMetadata from "../shared/PaperMetadata";
import PaperActions from "../shared/PaperActions";
import SearchResultHoverBackground from "../shared/SearchResultHoverBackground";

/**
 * @component SearchResultCompact
 * @description Compact horizontal view for search results
 */
export const SearchResultCompact = ({
  paper,
  index,
  text,
  textType,
  relativeLink,
  absoluteLink,
  externalLink,
  openDetailsInNewTab,
  onResultClick,
  onClickItem,
  badges,
  extraTags,
  showPaperDoi,
  isProcessingMetadata,
  hideCitations,
  showSourceCheckbox,
  showSaveBtn,
  showCiteBtn,
  showShareBtn,
  showManageUploadedPaperBtn,
  isLoadingExtractedAnswer,
  mixpanelActionSource,
  hashPaperRecordId,
  isUploading,
  isManageDropdownOpen,
  onManageDropdownOpenChange,
  onClickSave,
  onClickCite,
  onCitePaper,
  onFullTextClick,
  onSharePaper,
  researchHubUploadingStateLabels,
  recentlyUpdated,
  recentlyAdded,
  finalRanking,
  debugInfo,
  sequence,
  ...titleProps
}: SearchResultViewProps) => {
  const { paper_id } = paper;

  return (
    <div
      className={classNames(
        "relative w-full transition-colors duration-200 group @container",
        {
          "deep-search-yellow-static":
            recentlyUpdated && !recentlyAdded && !finalRanking,
          "deep-search-yellow": recentlyAdded,
          "deep-search-green": finalRanking,
        }
      )}
    >
      <SearchResultHoverBackground />
      <div
        className={classNames(
          "relative border-b scroll-mt-header cursor-pointer pt-4 pb-4 @lg:pb-2 flex items-center"
        )}
        data-intercom-target={`${sequence} result tile`}
        onClick={onResultClick}
        onMouseDown={(e) => {
          // Fire click events for left or middle mouse button clicks.
          if (e.button === 0 || e.button === 1)
            onClickItem?.(paper, index, textType);
        }}
      >
        <div className="flex-1">
          <Link
            href={relativeLink}
            target={externalLink || openDetailsInNewTab ? "_blank" : undefined}
            key={paper_id}
            data-testid="search-result"
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              if (titleProps.usePaperSidebar) {
                e.preventDefault();
              }
            }}
            data-test-text={absoluteLink}
          >
            <section>
              <PaperTitle {...titleProps} paper={paper} />
            </section>
          </Link>
          <section className="w-full">
            <div className="flex items-center justify-between w-full min-h-9 gap-4 mt-2">
              <SearchResultPaperMetadata
                paper={paper}
                badges={badges}
                extraTags={extraTags}
                showPaperDoi={showPaperDoi}
                isProcessingMetadata={isProcessingMetadata}
                hideCitations={hideCitations}
                resultFormat={titleProps.resultFormat}
                isUploadingLabels={researchHubUploadingStateLabels}
                showSourceCheckbox={showSourceCheckbox}
              />
              <PaperActions
                paper={paper}
                text={text}
                textType={textType}
                shareLink={absoluteLink}
                index={index}
                showSaveBtn={showSaveBtn}
                showCiteBtn={showCiteBtn}
                showShareBtn={showShareBtn}
                showManageUploadedPaperBtn={showManageUploadedPaperBtn}
                isLoadingExtractedAnswer={isLoadingExtractedAnswer}
                resultFormat={titleProps.resultFormat}
                mixpanelActionSource={mixpanelActionSource}
                hashPaperRecordId={hashPaperRecordId}
                isUploading={isUploading}
                isManageDropdownOpen={isManageDropdownOpen}
                onManageDropdownOpenChange={onManageDropdownOpenChange}
                onClickSave={onClickSave}
                onClickCite={onClickCite}
                onCitePaper={onCitePaper}
                onFullTextClick={onFullTextClick}
                onSharePaper={onSharePaper}
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default SearchResultCompact;
